import React, { useEffect, useState } from 'react';
import {
  Box, CssBaseline, Typography, Avatar, IconButton,
  Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, MenuItem, Select, FormControl, InputLabel, Chip,
  CircularProgress, Tooltip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Grid, Link,
  Breadcrumbs, TextField, InputAdornment
} from '@mui/material';
import {
  FilterList, DoneAll, AttachMoney, ReceiptLong, CalendarMonth, Search, Preview, Add, WarningAmber
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { pdf } from "@react-pdf/renderer";
import moment from 'moment';
import PayslipPDF from './Payslip';

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  filterBg: "#FFF9E6",
  accent: '#FFC107',
  accentDark: '#FFA000',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037'
};

const monthList = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const yearList = Array.from({ length: 5 }, (_, i) => (moment().year() - 2 + i).toString());

export default function TutorAttendanceList() {
  const navigate = useNavigate();

  // --- Attendance State ---
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [month, setMonth] = useState(moment().format("MMMM"));
  const [year, setYear] = useState(moment().format("YYYY"));

  // --- Search & Sorting State ---
  const [filterSearch, setFilterSearch] = useState("");
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('tutorName');

  // --- UI State ---
  const [openDetail, setOpenDetail] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [tutorList, setTutorList] = useState([]);
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [action, setAction] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form State for New Attendance
  const [newAttendance, setNewAttendance] = useState({
    tutorId: '',
    tutorName: '',
    month: moment().format("MMMM"),
    year: moment().format("YYYY")
  });

  useEffect(() => {
    localStorage.setItem("current_tab", "Attendance");
    getAttendanceData();
    getTutorList();
  }, []);

  const getTutorList = async () => {
    try {
      const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff`);
      const json = await res.json();
      setTutorList(json.data.filter(val => val.role === "Tutor"));
    } catch (error) { console.log(error); }
  };

  const getAttendanceData = async (targetMonth = month, targetYear = year) => {
    setLoading(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/tutor-attendance/${targetMonth}/${targetYear}`);
      const responseJson = await response.json();
      setAttendanceData(responseJson.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to fetch data", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- Duplicate Check Logic ---
  const isDuplicate = attendanceData.some(
    (item) =>
      item.tutorId === newAttendance.tutorId &&
      item.month === newAttendance.month &&
      item.year === newAttendance.year
  );

  const handleCreateAttendance = async () => {
    if (!newAttendance.tutorId) return alert("Please select a tutor");
    if (isDuplicate) return;

    setLoadingSubmit(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/tutor-attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAttendance, status: "Pending", classList: [] })
      });
      const res = await response.json();
      if (res.status === 1) {
        setSnackbar({ open: true, message: "Attendance record created!", severity: "success" });
        setOpenCreate(false);
        // Sync filter and refresh
        setMonth(newAttendance.month);
        setYear(newAttendance.year);
        getAttendanceData(newAttendance.month, newAttendance.year);
      } else {
        alert(res.message);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Creation failed", severity: "error" });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const updateStatus = async (id, status) => {
    setLoadingSubmit(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/tutor-attendance/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const res = await response.json();
      if (res.status === 1) {
        setSnackbar({ open: true, message: `Attendance marked as ${status}!`, severity: "success" });
        getAttendanceData();
        setOpenDetail(false);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Update failed", severity: "error" });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDownloadPayslip = async (data) => {
    const blob = await pdf(<PayslipPDF data={data} />).toBlob();
    saveAs(blob, `${data?.tutorName} (Payslip ${data.month} ${data.year}).pdf`);
  };

  const countTotalPayment = (classList) => {
    return classList?.reduce((sum, item) => sum + parseInt(item.payment || 0), 0) || 0;
  };

  const getStatusChip = (status) => {
    const configs = {
      'Paid': { color: 'success', label: 'Paid' },
      'Approved': { color: 'info', label: 'Approved' },
      'Pending': { color: 'warning', label: 'Pending' }
    };
    const config = configs[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" sx={{ fontWeight: 'bold' }} />;
  };

  const filteredAndSortedData = attendanceData
    .filter((row) => row.tutorName?.toLowerCase().includes(filterSearch.toLowerCase()))
    .sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
    });

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
          <Typography color="text.primary">Attendance</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ color: colors.textMain }}>Tutor Attendance Sheet</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpenCreate(true)} sx={{ bgcolor: colors.accentDark, color: 'black', borderRadius: 2 }}>
            Create New
          </Button>
        </Stack>

        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexGrow={1}>
            <FilterList sx={{ color: colors.textMain }} />
            <TextField
              size="small"
              placeholder="Search Tutor Name..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              sx={{ bgcolor: 'white', borderRadius: 2, minWidth: 250 }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)} sx={{ borderRadius: 2, bgcolor: 'white' }}>
                {monthList.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)} sx={{ borderRadius: 2, bgcolor: 'white' }}>
                {yearList.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Button variant="contained" onClick={() => getAttendanceData()} sx={{ borderRadius: 2, bgcolor: colors.accent, color: 'black', fontWeight: 'bold', px: 3, '&:hover': { bgcolor: colors.accentDark } }}>
            Search
          </Button>
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '60vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Tutor Name</TableCell>
                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Period</TableCell>
                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Total Payment</TableCell>
                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : filteredAndSortedData.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No data found.</TableCell></TableRow>
              ) : filteredAndSortedData.map((row) => (
                <TableRow key={row.attendanceId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: colors.accent, color: 'black', fontSize: 14 }}>{row.tutorName?.charAt(0)}</Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">{row.tutorName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{row.month} {row.year}</TableCell>
                  <TableCell align="center"><Typography fontWeight="bold" color="success.main">RM{countTotalPayment(row.classList)}</Typography></TableCell>
                  <TableCell align="center">{getStatusChip(row.status)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Edit Logs"><IconButton size="small" onClick={() => navigate(`/Portal/TutorAttendanceDetails/${row.attendanceId}`)}><CalendarMonth fontSize="small" color="action" /></IconButton></Tooltip>
                      <Tooltip title="View Summary"><IconButton size="small" onClick={() => { setAttendanceDetail(row); setOpenDetail(true); setAction("View"); }}><Preview fontSize="small" color="action" /></IconButton></Tooltip>
                      <Tooltip title="Approve"><IconButton size="small" color="info" onClick={() => { setAttendanceDetail(row); setOpenDetail(true); setAction("Approved"); }}><DoneAll fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Mark Paid"><IconButton size="small" color="success" onClick={() => { setAttendanceDetail(row); setOpenDetail(true); setAction("Paid"); }}><AttachMoney fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Download Payslip"><IconButton size="small" onClick={() => handleDownloadPayslip(row)}><ReceiptLong fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* --- CREATE NEW ATTENDANCE DIALOG --- */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Initialize Attendance Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Select Tutor" value={newAttendance.tutorId}
                onChange={(e) => {
                  const selected = tutorList.find(t => t.staffId === e.target.value);
                  setNewAttendance({ ...newAttendance, tutorId: e.target.value, tutorName: selected.fullname });
                }}>
                {tutorList.map(t => <MenuItem key={t.staffId} value={t.staffId}>{t.fullname}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Month" value={newAttendance.month} onChange={(e) => setNewAttendance({ ...newAttendance, month: e.target.value })}>
                {monthList.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Year" value={newAttendance.year} onChange={(e) => setNewAttendance({ ...newAttendance, year: e.target.value })}>
                {yearList.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            {isDuplicate && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                  A record already exists for <b>{newAttendance.tutorName}</b> in <b>{newAttendance.month} {newAttendance.year}</b>.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCreate(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={loadingSubmit || isDuplicate || !newAttendance.tutorId} onClick={handleCreateAttendance} sx={{ bgcolor: colors.accent, color: 'black', fontWeight: 'bold' }}>
            {loadingSubmit ? <CircularProgress size={20} /> : "Create Record"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- SNACKBAR & DETAIL DIALOG (Maintained as is) --- */}
      <Dialog fullWidth maxWidth="md" open={openDetail} onClose={() => setOpenDetail(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Details: {attendanceDetail?.tutorName}</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}><Typography variant="caption">Status</Typography><Box>{getStatusChip(attendanceDetail?.status)}</Box></Grid>
            <Grid item xs={6}><Typography variant="caption">Total</Typography><Typography fontWeight="bold" color="success.main">RM{countTotalPayment(attendanceDetail?.classList)}</Typography></Grid>
          </Grid>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EEE' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F9F9F9' }}><TableRow><TableCell>Date</TableCell><TableCell>Class</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>
                {attendanceDetail?.classList?.map((item, idx) => (
                  <TableRow key={idx}><TableCell>{item.date}</TableCell><TableCell>{item.courseName}</TableCell><TableCell align="right">RM{item.payment}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDetail(false)} color="inherit">Close</Button>
          {loadingSubmit ? <CircularProgress size={24} /> : (
            <>
              {action === "Approved" && <Button variant="contained" color="info" onClick={() => updateStatus(attendanceDetail.attendanceId, "Approved")}>Approve</Button>}
              {action === "Paid" && <Button variant="contained" color="success" onClick={() => updateStatus(attendanceDetail.attendanceId, "Paid")}>Mark Paid</Button>}
              {action === "Print" && <Button variant="contained" onClick={() => handleDownloadPayslip(attendanceDetail)}>Download PDF</Button>}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}